/**
 * Build-time environment resolution.
 *
 * ⚠️ VITE_* vars are inlined at BUILD time. The hard lesson we encode here:
 * a missing API URL must NEVER become a relative path (that yields 405s on
 * every API call). So we:
 *   - fall back to a sane DEV default (and WARN loudly), never to "" / relative;
 *   - THROW if an explicitly-set value is not an absolute http(s) URL.
 *
 * Set VITE_AUTH_API_URL / VITE_MASTERS_API_URL explicitly for QA/PROD builds.
 */

const AUTH_DEV_DEFAULT = "https://auth.dev.oxbo.energy";
const MASTERS_DEV_DEFAULT = "https://masters.dev.oxbo.energy";

function resolveUrl(key: keyof ImportMetaEnv, fallback: string): string {
  const raw = import.meta.env[key];

  if (!raw) {
    // Never silently relative — warn and use the explicit DEV default.
    console.warn(
      `[oxbo-admin] ${key} is not set at build time; falling back to "${fallback}". ` +
        `Set ${key} explicitly in your build/CI for QA/PROD.`,
    );
    return fallback;
  }

  if (!/^https?:\/\//i.test(raw)) {
    throw new Error(
      `[oxbo-admin] ${key}="${raw}" must be an absolute http(s) URL. ` +
        `A relative value turns API calls into same-origin requests (→ 405s).`,
    );
  }

  return raw.replace(/\/$/, "");
}

export const env = {
  authApiUrl: resolveUrl("VITE_AUTH_API_URL", AUTH_DEV_DEFAULT),
  mastersApiUrl: resolveUrl("VITE_MASTERS_API_URL", MASTERS_DEV_DEFAULT),
  /** Optional SSO bootstrap org (see .env.example). Empty until provisioned. */
  adminOrgId: import.meta.env.VITE_ADMIN_ORG_ID ?? "",
} as const;

export type Env = typeof env;
